import React, { useState } from 'react';
import styled from 'styled-components';
import { useSpring, animated } from 'react-spring';
import useMeasure from 'react-use-measure';
import { ResizeObserver } from '@juggle/resize-observer';
import { Link } from 'gatsby';

import P from '../styled/Paragraph';
import H from '../styled/Heading';
import B from '../styled/Button';

import RichText from '../shared/RichText';

import ArrowSvg from '../../assets/arrow-right.svg';
import XSvg from '../../assets/x.svg';

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  border-bottom: 2px solid ${({ theme }) => theme.colorMain};

  @media only screen and (max-width: 810px) {
    border-width: 1px;
  }

  &:last-of-type {
    border-bottom: 0;
  }
`;

const Title = styled.button`
  position: relative;
  width: 100%;
  text-align: left;
  padding: 10px 100px 10px 0;
  padding-bottom: ${({ hovered, open }) =>
    hovered && !open ? '25px' : '10px'};
  transition: padding 0.15s ease-in-out;

  @media only screen and (max-width: 810px) {
    padding: 4px 30px 4px 0;
  }
`;

const TitleText = styled(H)`
  line-height: 1;
  padding-top: 10px;
  
  @media only screen and (max-width: 810px) {
    line-height: 1;
  }
`;

const OpenClose = styled.div`
  position: absolute;
  right: 0;
  top: 42px;
  transition: transform 0.15s ease-in-out;
  transform: ${({ open }) => (open ? 'rotate(45deg)' : 'rotate(0deg)')};

  @media only screen and (max-width: 810px) {
    top: 14px;
  }
`;

const X = styled(XSvg)`
  height: 51px;
  width: 51px;

  @media only screen and (max-width: 810px) {
    height: 18px;
    width: 18px;
  }

  path,
  rect {
    fill: ${({ theme }) => theme.colorMain} !important;
  }
`;

const Categories = styled.div`
  margin-bottom: 14px;

  @media only screen and (max-width: 810px) {
    margin-bottom: 16px;
  }
`;

const Button = styled(B)`
  margin-right: 14px;
  margin-bottom: 7px;
  font-size: 20px;
  line-height: 24px;
  padding-bottom: 1px;

  @media only screen and (max-width: 810px) {
    font-size: 16px;
    line-height: 19px;
    margin-right: 7px;
  }
`;

const Text = styled(({ showBg, ...rest }) => <animated.div {...rest} />)`
  overflow: hidden;
`;

const TextInner = styled(P)`
  padding: 0 0 30px;
  max-width: 675px;
  width: 50%;
  min-height: 135px;

  @media only screen and (max-width: 810px) {
    width: 100%;
    max-width: 100%;
    padding: 0;
    min-height: 0;
  }
`;

const LinkWrapper = styled.span`
  @media only screen and (max-width: 810px) {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
`;

const LinkToProject = styled(H)`
  position: absolute;
  right: 0;
  bottom: 35px;
  padding: 14px 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-family: ${({ theme }) => theme.fontSans};

  @media only screen and (max-width: 810px) {
    display: inline-flex;
    position: relative;
    font-size: 24px;
    line-height: 29px;
    padding: 4px 7px;
    bottom: 12px;
  }
`;

const Arrow = styled(ArrowSvg)`
  width: 92px;
  height: 44px;

  @media only screen and (max-width: 810px) {
    width: 60px;
    height: 30px;
  }

  path,
  rect {
    stroke: ${({ theme }) => theme.colorMain} !important;
  }
`;

const Accordion = ({
  title,
  text,
  first = false,
  open,
  update,
  index,
  data,
  isEn,
}) => {
  const [hovered, setHovered] = useState(false);
  const [textRef, { height }] = useMeasure({ polyfill: ResizeObserver });

  const springProps = useSpring({ height: open ? height : 0 });
  return (
    <Wrapper first={first}>
      <Title
        onClick={() => update(index, !open)}
        open={open}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        hovered={hovered}
      >
        <TitleText size={1}>{data.title}</TitleText>
        <OpenClose open={open}>
          <X />
        </OpenClose>
      </Title>
      <Categories>
        {data.projectCategories.map(item => {
          return (
            <Button
              to={isEn ? `/en/works?cat=${item.slug}` : `/works?cat=${item.slug}`}
              small
              key={item.title}
            >
              {item.title}
            </Button>
          );
        })}
      </Categories>
      {data.intro && (
        <Text style={{ ...springProps }}>
          <div ref={textRef} style={{ position: 'relative' }}>
            <TextInner as="div">
              <RichText
                content={data.intro.raw}
                firstParMargin
              />
            </TextInner>
            <LinkWrapper>
              <LinkToProject
                size={6}
                as={Link}
                to={
                  isEn ? `/en/works/${data.slug}` : `/works/${data.slug}`
                }
              >
                {isEn ? 'See more' : 'Zobacz projekt'}
                <Arrow />
              </LinkToProject>
            </LinkWrapper>
          </div>
        </Text>
      )}
    </Wrapper>
  );
};

export default Accordion;
